import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ModalName = 'login' | 'forgot' | 'contact';

/**
 * Per-trigger variations for the contact modal — mirrors the current site's
 * useContactModal presets, which vary the title + submit button text
 * depending on which CTA opened it (e.g. "Start Modeling", "Contact Sales").
 */
export interface ContactOptions {
  title: string;
  submitText: string;
}

const DEFAULT_CONTACT: ContactOptions = {
  title: 'Contact Us',
  submitText: 'Submit Now',
};

interface ModalContextValue {
  active: ModalName | null;
  contact: ContactOptions;
  open: (name: ModalName, contact?: Partial<ContactOptions>) => void;
  close: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ModalName | null>(null);
  const [contact, setContact] = useState<ContactOptions>(DEFAULT_CONTACT);

  const open = useCallback(
    (name: ModalName, contactOpts?: Partial<ContactOptions>) => {
      if (name === 'contact') {
        setContact({ ...DEFAULT_CONTACT, ...contactOpts });
      }
      setActive(name);
    },
    [],
  );
  const close = useCallback(() => setActive(null), []);

  const value = useMemo(
    () => ({ active, contact, open, close }),
    [active, contact, open, close],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}
